import * as React from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  CalendarClock,
  ChevronDown,
  Clock,
  FolderPlus,
  Folders,
  LayoutGrid,
  Rows3,
  Search,
  UserRound,
} from 'lucide-react';
import {
  RENEWAL_KIND_LABEL,
  canRead,
  describeDaysLeft,
  renewalUrgency,
  type RoleName,
} from '@bits/domain';
import type {
  RenewalInput,
  RenewalKind,
  ProjectStatus,
  ProjectSummary,
  ProjectType,
} from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { SortableTh } from '@/components/ui/sortable-th';
import { sortRows, type SortAccessors, type SortState } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  component: ProjectsPage,
});

type RenewalRow = { label: string; provider: string; expiresOn: string };

type FormValues = {
  code: string;
  name: string;
  clientId: string;
  costCenterId: string;
  actualHoursOn: boolean;
  renewals: Record<QuickRenewalKind, RenewalRow>;
};

type QuickRenewalKind = Extract<RenewalKind, 'domain' | 'hosting' | 'amc'>;

const QUICK_RENEWALS: ReadonlyArray<{
  kind: QuickRenewalKind;
  labelPlaceholder: string;
  providerPlaceholder: string;
}> = [
  { kind: 'domain', labelPlaceholder: 'acme.in', providerPlaceholder: 'Registrar' },
  { kind: 'hosting', labelPlaceholder: 'Cloud Startup plan', providerPlaceholder: 'Host' },
  { kind: 'amc', labelPlaceholder: 'Annual maintenance', providerPlaceholder: 'Vendor' },
];

const EMPTY_RENEWALS: Record<QuickRenewalKind, RenewalRow> = {
  domain: { label: '', provider: '', expiresOn: '' },
  hosting: { label: '', provider: '', expiresOn: '' },
  amc: { label: '', provider: '', expiresOn: '' },
};

function emptyForm(costCenterId: string): FormValues {
  return {
    code: '',
    name: '',
    clientId: '',
    costCenterId,
    actualHoursOn: false,
    renewals: EMPTY_RENEWALS,
  };
}

function renewalsFromForm(
  rows: Record<QuickRenewalKind, RenewalRow>,
): Omit<RenewalInput, 'projectId'>[] {
  return QUICK_RENEWALS.flatMap(({ kind }) => {
    const row = rows[kind];
    const label = row.label.trim();
    const provider = row.provider.trim();
    if (!label && !provider && !row.expiresOn) return [];
    return [
      {
        kind,
        label: label || RENEWAL_KIND_LABEL[kind],
        provider: provider || null,
        expiresOn: row.expiresOn || null,
      },
    ];
  });
}

type StatusFilter = ProjectStatus | '';
type SortKey = 'recent' | 'name' | 'code';
type ProjectSortKey =
  | 'code'
  | 'project'
  | 'client'
  | 'type'
  | 'status'
  | 'manager'
  | 'hours'
  | 'renewal';

const STATUS_FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'closed', label: 'Closed' },
  { value: '', label: 'All' },
];

const STATUS_LABEL: Record<ProjectStatus, string> = {
  active: 'Active',
  on_hold: 'On hold',
  closed: 'Closed',
};

const TYPE_LABEL: Record<ProjectType, string> = {
  client_delivery: 'Client delivery',
  internal_initiative: 'Internal initiative',
};

const UNGROUPED = '__ungrouped__';

function formatCreated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

type ProjectsView = 'cards' | 'table';
const VIEW_PREF_KEY = 'projects.view';

function readViewPreference(): ProjectsView {
  try {
    return window.localStorage.getItem(VIEW_PREF_KEY) === 'table' ? 'table' : 'cards';
  } catch {
    return 'cards';
  }
}

function sortProjects(list: ProjectSummary[], sort: SortKey): ProjectSummary[] {
  return [...list].sort((a, b) => {
    if (sort === 'name') return a.name.localeCompare(b.name);
    if (sort === 'code') return a.code.localeCompare(b.code);
    return b.createdAt.localeCompare(a.createdAt);
  });
}

function ProjectsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('active');
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<SortKey>('recent');
  const [groupByClient, setGroupByClient] = React.useState(true);
  const [view, setView] = React.useState<ProjectsView>(readViewPreference);
  React.useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_PREF_KEY, view);
    } catch {
      // Remembering the layout is a convenience; a blocked store is not an error.
    }
  }, [view]);
  const [renewalsOpen, setRenewalsOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['projects', statusFilter, search],
    queryFn: () =>
      api.listProjects({
        status: statusFilter || undefined,
        search: search || undefined,
      }),
  });
  const clientsQuery = useQuery({
    queryKey: ['clients-for-projects'],
    queryFn: () => api.listClientsSimple(),
    retry: false,
  });
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
    retry: false,
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  // Decided from the signed-in roles rather than from a failed request, so the
  // cost-center field is either present for the whole dialog or never
  // registered: react-hook-form keeps a field's rules once register() has run,
  // even if the input is later dropped from the tree.
  const canReadCostCenters = canRead(
    (meQuery.data?.person.roles ?? []) as RoleName[],
    'cost_centers',
  );
  const costCentersQuery = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => api.listCostCenters(),
    enabled: canReadCostCenters,
  });

  const overheadId = React.useMemo(() => {
    const list = costCentersQuery.data ?? [];
    return list.find((c) => c.isDefaultOverhead && c.active)?.id ?? '';
  }, [costCentersQuery.data]);

  const form = useForm<FormValues>({ defaultValues: emptyForm('') });

  React.useEffect(() => {
    if (overheadId && !form.getValues('costCenterId')) {
      form.setValue('costCenterId', overheadId);
    }
  }, [overheadId, form]);

  const watchedClientId = form.watch('clientId');
  const codePreview = React.useMemo(() => {
    const client = (clientsQuery.data ?? []).find((c) => c.id === watchedClientId);
    if (!client) return null;
    const stem = client.displayName
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 4)
      .padEnd(4, 'X');
    return `${stem}-${new Date().getFullYear()}-NN`;
  }, [watchedClientId, clientsQuery.data]);

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => {
      const type: ProjectType = 'client_delivery';
      return api.createProject({
        code: values.code.trim() || undefined,
        name: values.name,
        type,
        clientId: values.clientId || undefined,
        costCenterId: values.costCenterId || undefined,
        actualHoursOn: values.actualHoursOn,
        renewals: renewalsFromForm(values.renewals),
      });
    },
    onSuccess: async (res) => {
      setDialogOpen(false);
      setErrorMessage(null);
      setRenewalsOpen(false);
      form.reset(emptyForm(overheadId));
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await navigate({ to: '/projects/$projectId', params: { projectId: res.projectId } });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const projects = listQuery.data ?? [];
  const clients = clientsQuery.data ?? [];
  const costCenters = costCentersQuery.data ?? [];

  const clientNames = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients) map.set(c.id, c.displayName);
    return map;
  }, [clients]);

  const personNames = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const p of peopleQuery.data ?? []) map.set(p.id, p.name);
    return map;
  }, [peopleQuery.data]);

  const canGroup = clientNames.size > 0;

  const groups = React.useMemo(() => {
    const sorted = sortProjects(projects, sort);
    if (!groupByClient || !canGroup) return [{ key: UNGROUPED, label: null, items: sorted }];
    const byClient = new Map<string, ProjectSummary[]>();
    for (const p of sorted) {
      const key = p.clientId ?? UNGROUPED;
      const bucket = byClient.get(key);
      if (bucket) bucket.push(p);
      else byClient.set(key, [p]);
    }
    return [...byClient.entries()]
      .map(([key, items]) => ({
        key,
        label:
          key === UNGROUPED ? 'Internal & unassigned' : (clientNames.get(key) ?? 'Other clients'),
        items,
      }))
      .sort((a, b) => {
        if (a.key === UNGROUPED) return 1;
        if (b.key === UNGROUPED) return -1;
        return (a.label ?? '').localeCompare(b.label ?? '');
      });
  }, [projects, sort, groupByClient, canGroup, clientNames]);

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Projects
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Client delivery projects with budgets, tasks, and monthly people allocation.
          </p>
        </div>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setErrorMessage(null);
              setRenewalsOpen(false);
              form.reset(emptyForm(overheadId));
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <FolderPlus />
              New project
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New project</DialogTitle>
              <DialogDescription>
                Client delivery only. Cost center defaults to Overhead.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
              className="flex flex-col gap-3"
            >
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Code"
                  hint={
                    codePreview
                      ? `Leave blank to use ${codePreview}`
                      : 'Leave blank to auto-generate'
                  }
                  error={form.formState.errors.code?.message}
                >
                  <Input
                    {...form.register('code', {
                      validate: (v) =>
                        v === '' || /^[A-Z0-9._-]+$/i.test(v) || 'Letters, digits, . _ - only',
                    })}
                    placeholder={codePreview ?? 'auto'}
                  />
                </Field>
                <Field label="Name" required error={form.formState.errors.name?.message}>
                  <Input {...form.register('name', { required: 'Enter a project name' })} />
                </Field>
                <Field label="Client" required error={form.formState.errors.clientId?.message}>
                  <Select {...form.register('clientId', { required: 'Pick a client' })}>
                    <option value="">Select client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName}
                      </option>
                    ))}
                  </Select>
                </Field>
                {canReadCostCenters ? (
                  <Field
                    label="Cost center"
                    required
                    error={form.formState.errors.costCenterId?.message}
                  >
                    <Select {...form.register('costCenterId', { required: 'Pick a cost center' })}>
                      <option value="">Select…</option>
                      {costCenters
                        .filter((c) => c.active)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                            {c.isDefaultOverhead ? ' (default)' : ''}
                          </option>
                        ))}
                    </Select>
                  </Field>
                ) : null}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...form.register('actualHoursOn')} />
                Track actual hours on this project
              </label>

              <div className="rounded-xl border border-[hsl(var(--border))]">
                <button
                  type="button"
                  onClick={() => setRenewalsOpen((v) => !v)}
                  aria-expanded={renewalsOpen}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm"
                >
                  <span className="flex items-center gap-2 font-medium">
                    <CalendarClock
                      className="size-4 text-[hsl(var(--muted-foreground))]"
                      aria-hidden
                    />
                    Hosting, domain & AMC
                    <span className="font-normal text-[hsl(var(--muted-foreground))]">
                      optional
                    </span>
                  </span>
                  <ChevronDown
                    className={cn(
                      'size-4 text-[hsl(var(--muted-foreground))] transition-transform',
                      renewalsOpen && 'rotate-180',
                    )}
                    aria-hidden
                  />
                </button>
                {renewalsOpen ? (
                  <div className="flex flex-col gap-3 border-t border-[hsl(var(--border))] px-4 py-3">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      Fill what you know now. Everything here can be edited later from the project’s
                      Hosting & renewals tab, where Admins get expiry reminders.
                    </p>
                    {QUICK_RENEWALS.map((q) => (
                      <div
                        key={q.kind}
                        className="grid grid-cols-[88px_1fr_1fr_150px] items-center gap-2"
                      >
                        <span className="text-sm font-medium">{RENEWAL_KIND_LABEL[q.kind]}</span>
                        <Input
                          aria-label={`${RENEWAL_KIND_LABEL[q.kind]} label`}
                          placeholder={q.labelPlaceholder}
                          className="h-10"
                          {...form.register(`renewals.${q.kind}.label`)}
                        />
                        <Input
                          aria-label={`${RENEWAL_KIND_LABEL[q.kind]} provider`}
                          placeholder={q.providerPlaceholder}
                          className="h-10"
                          {...form.register(`renewals.${q.kind}.provider`)}
                        />
                        <Input
                          aria-label={`${RENEWAL_KIND_LABEL[q.kind]} expiry date`}
                          type="date"
                          className="h-10"
                          {...form.register(`renewals.${q.kind}.expiresOn`)}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Create failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Saving…' : 'Create project'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
          />
          <Input
            className="pl-9"
            placeholder="Search by code or name…"
            aria-label="Search projects"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <div
          role="tablist"
          aria-label="Status"
          className="flex rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-0.5"
        >
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.value || 'all'}
              type="button"
              role="tab"
              aria-selected={statusFilter === s.value}
              onClick={() => setStatusFilter(s.value)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm transition-colors',
                statusFilter === s.value
                  ? 'bg-[hsl(var(--brand))] text-white'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <Select
          aria-label="Sort projects"
          className="h-11 w-auto min-w-[170px]"
          value={sort}
          onChange={(e) => setSort(e.currentTarget.value as SortKey)}
        >
          <option value="recent">Newest first</option>
          <option value="name">Name A–Z</option>
          <option value="code">Code A–Z</option>
        </Select>
        {canGroup ? (
          <button
            type="button"
            role="switch"
            aria-checked={groupByClient}
            onClick={() => setGroupByClient((v) => !v)}
            className={cn(
              'inline-flex h-11 items-center gap-2 rounded-xl border px-4 text-sm transition-colors',
              groupByClient
                ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                : 'border-[hsl(var(--input))] bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]',
            )}
          >
            <Folders className="size-4" aria-hidden />
            Group by client
          </button>
        ) : null}
        <div
          role="radiogroup"
          aria-label="Layout"
          className="inline-flex h-11 items-center rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--card))] p-1"
        >
          {(
            [
              { value: 'cards', label: 'Cards', Icon: LayoutGrid },
              { value: 'table', label: 'Table', Icon: Rows3 },
            ] as const
          ).map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={view === value}
              onClick={() => setView(value)}
              className={cn(
                'inline-flex h-full items-center gap-1.5 rounded-lg px-3 text-sm transition-colors',
                view === value
                  ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] font-medium'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]',
              )}
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-[hsl(var(--muted-foreground))]">
        {listQuery.isPending
          ? 'Loading projects…'
          : `${projects.length} ${projects.length === 1 ? 'project' : 'projects'}${
              statusFilter ? ` · ${STATUS_LABEL[statusFilter].toLowerCase()}` : ''
            }${search ? ` · matching “${search}”` : ''}`}
      </p>

      {listQuery.isPending ? (
        <FolderGridSkeleton />
      ) : projects.length === 0 ? (
        <EmptyState
          title={search ? 'No projects match' : 'No projects here'}
          body={
            search
              ? 'Try a different code or name, or widen the status filter.'
              : statusFilter === 'active'
                ? 'Create a project to start tracking budgets, tasks and allocation.'
                : `There are no ${STATUS_LABEL[statusFilter || 'active'].toLowerCase()} projects.`
          }
          action={
            search ? (
              <Button variant="outline" size="sm" onClick={() => setSearch('')}>
                Clear search
              </Button>
            ) : statusFilter === 'active' ? (
              <Button onClick={() => setDialogOpen(true)}>
                <FolderPlus />
                New project
              </Button>
            ) : null
          }
        />
      ) : view === 'table' ? (
        <ProjectTable groups={groups} clientNames={clientNames} personNames={personNames} />
      ) : (
        <div className="flex flex-col gap-8">
          {groups.map((g) => (
            <section key={g.key} className="flex flex-col gap-3">
              {g.label ? (
                <div className="flex items-baseline gap-2">
                  <h2 className="font-display text-lg font-semibold">{g.label}</h2>
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">
                    {g.items.length}
                  </span>
                </div>
              ) : null}
              <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {g.items.map((p) => (
                  <li key={p.id}>
                    <FolderCard
                      project={p}
                      clientName={p.clientId ? (clientNames.get(p.clientId) ?? null) : null}
                      managerName={p.managerId ? (personNames.get(p.managerId) ?? null) : null}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </AppShell>
  );
}

const FOLDER_TONES: Record<
  ProjectStatus,
  { tab: string; body: string; dot: string; label: string }
> = {
  active: {
    tab: 'bg-[hsl(var(--brand))]',
    body: 'border-[hsl(var(--brand))]/25 bg-[hsl(var(--card))] group-hover:border-[hsl(var(--brand))]/60',
    dot: 'bg-emerald-500',
    label: 'text-[hsl(var(--foreground))]',
  },
  on_hold: {
    tab: 'bg-amber-400',
    body: 'border-amber-300/60 bg-[hsl(var(--card))] group-hover:border-amber-400',
    dot: 'bg-amber-500',
    label: 'text-amber-800',
  },
  closed: {
    tab: 'bg-[hsl(var(--muted-foreground))]/45',
    body: 'border-[hsl(var(--border))] bg-[hsl(var(--muted))]/60 group-hover:border-[hsl(var(--muted-foreground))]/50',
    dot: 'bg-[hsl(var(--muted-foreground))]',
    label: 'text-[hsl(var(--muted-foreground))]',
  },
};

function FolderCard({
  project,
  clientName,
  managerName,
}: {
  project: ProjectSummary;
  clientName: string | null;
  managerName: string | null;
}): JSX.Element {
  const tone = FOLDER_TONES[project.status];
  const closed = project.status === 'closed';
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      className="group relative block h-full pt-3 transition-transform duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--background))] rounded-2xl"
      aria-label={`${project.code} ${project.name}, ${STATUS_LABEL[project.status]}`}
    >
      <span
        aria-hidden
        className={cn(
          'absolute left-0 top-0 h-5 w-[42%] min-w-[96px] rounded-t-xl transition-colors',
          tone.tab,
        )}
        style={{ clipPath: 'polygon(0 0, 86% 0, 100% 100%, 0 100%)' }}
      />
      <div
        className={cn(
          'relative flex h-full min-h-[172px] flex-col gap-4 rounded-2xl rounded-tl-none border p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.10)] transition-colors group-hover:shadow-[0_1px_2px_rgba(16,24,40,0.06),0_14px_32px_-12px_rgba(16,24,40,0.18)]',
          tone.body,
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <code
            className={cn(
              'rounded-md px-1.5 py-0.5 font-mono text-xs font-medium',
              closed
                ? 'bg-[hsl(var(--background))] text-[hsl(var(--muted-foreground))]'
                : 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
            )}
          >
            {project.code}
          </code>
          <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', tone.label)}>
            <span aria-hidden className={cn('size-2 rounded-full', tone.dot)} />
            {STATUS_LABEL[project.status]}
          </span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-1">
          <h3
            className={cn(
              'line-clamp-2 font-display text-lg font-semibold leading-snug',
              closed ? 'text-[hsl(var(--muted-foreground))]' : 'text-[hsl(var(--foreground))]',
            )}
          >
            {project.name}
          </h3>
          <p className="truncate text-sm text-[hsl(var(--muted-foreground))]">
            {clientName ?? TYPE_LABEL[project.type]}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[hsl(var(--border))]/70 pt-3 text-xs text-[hsl(var(--muted-foreground))]">
          <span className="inline-flex min-w-0 items-center gap-2">
            {managerName ? (
              <>
                <span
                  aria-hidden
                  className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--secondary))] text-[10px] font-semibold text-[hsl(var(--secondary-foreground))]"
                >
                  {initials(managerName)}
                </span>
                <span className="truncate">{managerName}</span>
              </>
            ) : (
              <>
                <UserRound className="size-3.5 shrink-0" aria-hidden />
                <span>No manager</span>
              </>
            )}
          </span>
          <span className="inline-flex shrink-0 items-center gap-3">
            {project.actualHoursOn ? (
              <span className="inline-flex items-center gap-1" title="Actual hours tracked">
                <Clock className="size-3.5" aria-hidden />
                Hours
              </span>
            ) : null}
            {project.nextRenewal ? (
              <RenewalChip renewal={project.nextRenewal} />
            ) : (
              <span title={`Created ${formatCreated(project.createdAt)}`}>
                {formatCreated(project.createdAt)}
              </span>
            )}
          </span>
        </div>
      </div>
    </Link>
  );
}

function RenewalChip({
  renewal,
}: {
  renewal: NonNullable<ProjectSummary['nextRenewal']>;
}): JSX.Element {
  const urgency = renewalUrgency(renewal.daysLeft);
  const hot = urgency === 'expired' || urgency === 'today' || urgency === 'critical';
  const warm = urgency === 'soon';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium',
        hot && 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]',
        warm && 'bg-amber-100 text-amber-800',
        !hot && !warm && 'text-[hsl(var(--muted-foreground))]',
      )}
      title={`${RENEWAL_KIND_LABEL[renewal.kind]} · ${renewal.label} · ${renewal.expiresOn}`}
    >
      <CalendarClock className="size-3.5" aria-hidden />
      {RENEWAL_KIND_LABEL[renewal.kind]} {describeDaysLeft(renewal.daysLeft)}
    </span>
  );
}

function ProjectTable({
  groups,
  clientNames,
  personNames,
}: {
  groups: { key: string; label: string | null; items: ProjectSummary[] }[];
  clientNames: Map<string, string>;
  personNames: Map<string, string>;
}): JSX.Element {
  const navigate = useNavigate();
  const open = (id: string): void => {
    void navigate({ to: '/projects/$projectId', params: { projectId: id } });
  };

  const [sort, setSort] = React.useState<SortState<ProjectSortKey> | null>(null);
  const toggle = React.useCallback((key: ProjectSortKey) => {
    setSort((current) => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);
  const sorter = { sort, toggle };

  const sortAccessors = React.useMemo<SortAccessors<ProjectSummary, ProjectSortKey>>(
    () => ({
      code: (p) => p.code,
      project: (p) => p.name,
      client: (p) => (p.clientId ? (clientNames.get(p.clientId) ?? null) : null),
      type: (p) => TYPE_LABEL[p.type],
      status: (p) => STATUS_LABEL[p.status],
      manager: (p) => (p.managerId ? (personNames.get(p.managerId) ?? null) : null),
      hours: (p) => p.actualHoursOn,
      renewal: (p) => p.nextRenewal?.expiresOn ?? null,
    }),
    [clientNames, personNames],
  );
  const sortedGroups = React.useMemo(
    () => groups.map((g) => ({ ...g, items: sortRows(g.items, sortAccessors, sort) })),
    [groups, sortAccessors, sort],
  );

  return (
    <div className="overflow-x-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
      <table className="w-full min-w-[880px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/60 text-left text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            <SortableTh sortKey="code" controller={sorter} className="py-3 pl-5 pr-3 font-medium">
              Code
            </SortableTh>
            <SortableTh sortKey="project" controller={sorter} className="px-3 py-3 font-medium">
              Project
            </SortableTh>
            <SortableTh sortKey="client" controller={sorter} className="px-3 py-3 font-medium">
              Client
            </SortableTh>
            <SortableTh sortKey="type" controller={sorter} className="px-3 py-3 font-medium">
              Type
            </SortableTh>
            <SortableTh sortKey="status" controller={sorter} className="px-3 py-3 font-medium">
              Status
            </SortableTh>
            <SortableTh sortKey="manager" controller={sorter} className="px-3 py-3 font-medium">
              Manager
            </SortableTh>
            <SortableTh sortKey="hours" controller={sorter} className="px-3 py-3 font-medium">
              Hours
            </SortableTh>
            <SortableTh sortKey="renewal" controller={sorter} className="py-3 pl-3 pr-5 font-medium">
              Next renewal
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {sortedGroups.map((g) => (
            <React.Fragment key={g.key}>
              {g.label ? (
                <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                  <th
                    colSpan={8}
                    scope="colgroup"
                    className="py-2 pl-5 pr-3 text-left font-display text-sm font-semibold"
                  >
                    {g.label}{' '}
                    <span className="font-sans text-xs font-normal text-[hsl(var(--muted-foreground))]">
                      {g.items.length}
                    </span>
                  </th>
                </tr>
              ) : null}
              {g.items.map((p) => {
                const tone = FOLDER_TONES[p.status];
                const clientName = p.clientId ? (clientNames.get(p.clientId) ?? null) : null;
                const managerName = p.managerId ? (personNames.get(p.managerId) ?? null) : null;
                return (
                  <tr
                    key={p.id}
                    onClick={() => open(p.id)}
                    className="cursor-pointer border-b border-[hsl(var(--border))] last:border-b-0 transition-colors hover:bg-[hsl(var(--accent))]/50"
                  >
                    <td className="py-3 pl-5 pr-3">
                      <code className="whitespace-nowrap rounded-md bg-[hsl(var(--accent))] px-1.5 py-0.5 font-mono text-xs font-medium text-[hsl(var(--accent-foreground))]">
                        {p.code}
                      </code>
                    </td>
                    <td className="px-3 py-3">
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: p.id }}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-[hsl(var(--foreground))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded"
                      >
                        {p.name}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-[hsl(var(--muted-foreground))]">
                      {clientName ?? '—'}
                    </td>
                    <td className="px-3 py-3 text-[hsl(var(--muted-foreground))]">
                      {TYPE_LABEL[p.type]}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 text-xs font-medium',
                          tone.label,
                        )}
                      >
                        <span aria-hidden className={cn('size-2 rounded-full', tone.dot)} />
                        {STATUS_LABEL[p.status]}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[hsl(var(--muted-foreground))]">
                      {managerName ?? 'No manager'}
                    </td>
                    <td className="px-3 py-3 text-[hsl(var(--muted-foreground))]">
                      {p.actualHoursOn ? 'Tracked' : '—'}
                    </td>
                    <td className="py-3 pl-3 pr-5 text-xs text-[hsl(var(--muted-foreground))]">
                      {p.nextRenewal ? (
                        <RenewalChip renewal={p.nextRenewal} />
                      ) : (
                        <span title="No renewals">Created {formatCreated(p.createdAt)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FolderGridSkeleton(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" aria-busy>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="relative pt-3">
          <span className="absolute left-0 top-0 h-5 w-[42%] rounded-t-xl bg-[hsl(var(--muted))]" />
          <div className="flex min-h-[172px] flex-col gap-4 rounded-2xl rounded-tl-none border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <div className="h-5 w-24 animate-pulse rounded bg-[hsl(var(--muted))]" />
            <div className="flex flex-col gap-2">
              <div className="h-4 w-3/4 animate-pulse rounded bg-[hsl(var(--muted))]" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-[hsl(var(--muted))]" />
            </div>
            <div className="mt-auto h-3 w-full animate-pulse rounded bg-[hsl(var(--muted))]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[hsl(var(--border))] px-8 py-16 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
        <Folders className="size-5" aria-hidden />
      </span>
      <div className="flex flex-col gap-1">
        <p className="font-medium">{title}</p>
        <p className="max-w-sm text-sm text-[hsl(var(--muted-foreground))]">{body}</p>
      </div>
      {action}
    </div>
  );
}
